// Tenký sink do GA4 přes nativní `gtag()` Shoptetu (měření vyhledávání, #68).
//
// PROČ `gtag()` A NE `dataLayer.push({event})`: GA4 na ostré plní VÝHRADNĚ
// nativní gtag Shoptetu (`gtag('config','G-…',{groups:'GA4', server_container_url})`
// → hity na sGTM `nxa.jipos.cz`). GA4 tagy v našem web GTM jsou pauznuté,
// takže holý `dataLayer.push({event:'…'})` v GA4 nikam nedojde. Luigi's Box
// šel přesně touhle cestou (`gtag('event','luigisbox',{send_to:<G-id>})`).
//
// `send_to` NIKDY nevynechat: bez něj by event šel i do Google Ads
// (`AW-…` je druhý cíl téhož gtag) jako remarketing hit. Cíl se bere z
// `config` entry v dataLayeru (jako LB), fallback je jméno skupiny `GA4`
// ze Shoptetího configu — neexistující cíl gtag tiše zahodí.
//
// Bez `window.gtag` (dev instance, GA nenakonfigurováno) se do `dataLayer`
// pushne Arguments objekt — přesně to, co dělá `gtag()` sám; později načtený
// gtag.js ho zpracuje stejně a harness na dev z něj čte, co by odešlo.
// Pole `['event', …]` gtag ignoruje, musí to být `arguments`.
//
// Souhlas: NEGATUJE se v JS — Google Consent Mode řeší souhlas sám (stejně
// jako nativní `search`/`view_item` Shoptetu i LB). Kdo tuhle vrstvu smí
// vůbec pustit, rozhoduje `searchConfig.getConfig()` u volajícího.
//
// ⚠️ ZÁSADA (jako searchAnalytics.js): měření nikdy nesmí shodit render.
// Všechno v try/catch, všechno synchronní, nic neblokuje navigaci.

const FALLBACK_TARGET = 'GA4';
const MAX_LEN = 100; // GA4 ořezává hodnoty parametrů na 100 znaků — řežeme sami

let sendTo = null;
let baseSettings = null;

/**
 * První `gtag('config','G-…', {…})` v dataLayeru (LB postup) — měřicí ID
 * i jeho nastavení. Cache: config se během stránky nemění.
 */
function findConfig() {
  if (sendTo) return { id: sendTo, settings: baseSettings };
  try {
    const dl = window.dataLayer;
    if (dl && dl.length) {
      for (let i = 0; i < dl.length; i++) {
        const entry = dl[i];
        if (entry && entry[0] === 'config' && typeof entry[1] === 'string' && /^G-/.test(entry[1])) {
          sendTo = entry[1];
          baseSettings = entry[2] && typeof entry[2] === 'object' ? entry[2] : null;
          return { id: sendTo, settings: baseSettings };
        }
      }
    }
  } catch (e) {
    /* dataLayer ještě není */
  }
  return { id: null, settings: null };
}

/** Cíl pro `send_to`: měřicí ID z configu, jinak jméno skupiny. */
export function resolveSendTo() {
  return findConfig().id || FALLBACK_TARGET;
}

/** Arguments objekt — jediný tvar, který gtag.js z fronty čte. */
function args() {
  return arguments;
}

/**
 * Nízkoúrovňové volání: `gtag(...)` když existuje, jinak push Arguments do
 * `dataLayer`. Předává se přesně tolik argumentů, kolik přišlo — `set` má
 * dva, `event` tři; `undefined` navíc by gtag mohl číst jako parametr.
 */
export function ga4() {
  try {
    const list = Array.prototype.slice.call(arguments);
    if (typeof window.gtag === 'function') {
      window.gtag.apply(window, list);
      return true;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(args.apply(null, list));
    return true;
  } catch (e) {
    return false;
  }
}

function clean(value) {
  if (value === null || value === undefined || value === '') return undefined;
  if (typeof value === 'string') return value.length > MAX_LEN ? value.slice(0, MAX_LEN) : value;
  return value;
}

/** Vyčistí parametry: pryč prázdné, řetězce na 100 znaků, `items` nechá být. */
function cleanParams(params) {
  const out = {};
  Object.keys(params || {}).forEach(function (key) {
    const value = key === 'items' ? params[key] : clean(params[key]);
    if (value !== undefined) out[key] = value;
  });
  return out;
}

/** `gtag('event', name, {send_to, …params})`. */
export function ga4Event(name, params) {
  try {
    const payload = cleanParams(params);
    payload.send_to = resolveSendTo();
    return ga4('event', name, payload);
  } catch (e) {
    return false;
  }
}

/**
 * Parametry, které GA4 přibalí ke KAŽDÉMU dalšímu eventu měřicího ID —
 * i k nativním eventům Shoptetu (`add_to_cart` po AJAXu košíku).
 *
 * ⚠️ NE `gtag('set', {…})`: dokumentovaná cesta, která na ostré **nefunguje**
 * — parametr se k hitům na `nxa.jipos.cz/g/collect` nepřibalil ani ze snippetu
 * v hlavičce, ani odsud (sonda 2026-09-11, `ops/sweeps/2026-09-11-ga4-search-session`,
 * #326; dev to chytit nemohl — GA4 tam není a harness ověřuje stub). Co funguje:
 * druhý `gtag('config', '<G-id>', {…})` po načtení — parametry nesou další
 * eventy a se `send_page_view:false` nevznikne druhý `page_view`.
 *
 * ⚠️ Druhý `config` PŘEPISUJE nastavení měřicího ID, takže musí zopakovat
 * to Shoptetí — jinak se ztratí `server_container_url` a další hity odletí
 * místo na first-party sGTM `nxa.jipos.cz` na `region1.analytics.google.com`
 * (změřeno `tools/devcheck/search-ga4-live.mjs` na ostré 2026-09-11), a s ním
 * i `content_group` a spol. Nastavení se proto kopíruje z prvního configu.
 *
 * Bez známého `G-` cíle (dev instance bez GA4, fallback skupina `GA4`) se
 * NEPOSÍLÁ nic: `config` na jméno skupiny by nedávalo smysl.
 */
export function ga4Set(params) {
  try {
    const payload = cleanParams(params);
    if (!Object.keys(payload).length) return false;
    const cfg = findConfig();
    if (!cfg.id || !/^G-/.test(cfg.id)) return false;
    const out = {};
    if (cfg.settings) Object.keys(cfg.settings).forEach((key) => { out[key] = cfg.settings[key]; });
    Object.keys(payload).forEach((key) => { out[key] = payload[key]; });
    out.send_page_view = false;
    return ga4('config', cfg.id, out);
  } catch (e) {
    return false;
  }
}

/** Jen pro testy — zapomene cachovaný cíl i nastavení. */
export function resetGa4() {
  sendTo = null;
  baseSettings = null;
}
